import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { useMemo, useState } from "react";

import { monospaceStyle } from "../../theme";
import {
  ProductSpecification,
  SpecificationStatement,
} from "../../types/productPlane";
import RowToggle, { NoToggle, TOGGLE_WIDTH } from "./RowToggle";
import SourcesChip from "./SourcesChip";
import Tag from "./Tag";
import { columnHeader, quiet, tableFrame } from "./claimCells";
import { codeWords, formatSpecificationValue } from "./format";
import { SpecificationRow, specificationModel } from "./specificationModel";

export interface SpecificationTableProps {
  // One entry per key and unit, each with its statements, newest first
  specifications: ProductSpecification[];
  // A plain table for a page that is printed: each metric's value, no
  // quoted wording, no control, no sources column
  print?: boolean;
}

const monospaceCell = { ...monospaceStyle, whiteSpace: "nowrap" } as const;

// The gap (px) between the toggle and the key
const TOGGLE_GAP = 6;

const COLUMNS = ["Key", "Value", "Unit", "Qualifier", "Sources"];

// The columns of the printed table: all but the sources
const PRINT_COLUMNS = COLUMNS.slice(0, -1);

// What stands beneath a key: the document's wording, and who states it
const StatementWording: React.FC<{ statement: SpecificationStatement }> = ({
  statement,
}) => (
  <>
    {statement.wording && (
      <Typography variant="caption" component="div" sx={quiet}>
        “{statement.wording}”
      </Typography>
    )}
    {statement.attribution && (
      <Typography variant="caption" component="div" sx={quiet}>
        {statement.attribution}
      </Typography>
    )}
  </>
);

// The cells of a value: value, unit, qualifier with its conditions, and
// the sources except on a printed page
const ValueCells: React.FC<{
  value: SpecificationRow["value"];
  display: SpecificationRow["display"];
  qualifier: string | null;
  conditions: string | null;
  sources: SpecificationStatement["sources"];
  print?: boolean;
}> = ({ value, display, qualifier, conditions, sources, print = false }) => (
  <>
    <TableCell align="right" sx={monospaceCell}>
      {formatSpecificationValue(value, display.divisor)}
    </TableCell>
    <TableCell sx={{ ...monospaceCell, ...quiet }}>{display.unit ?? ""}</TableCell>
    <TableCell sx={quiet}>
      {codeWords(qualifier)}
      {conditions && (
        <Typography variant="caption" component="div" sx={quiet}>
          {conditions}
        </Typography>
      )}
    </TableCell>
    {!print && (
      <TableCell>
        <SourcesChip sources={sources} />
      </TableCell>
    )}
  </>
);

// One metric: its row of the value, or the range its statements span, over
// the sources of all of them; opened, every statement beneath it
const SpecificationRows: React.FC<{
  row: SpecificationRow;
  print: boolean;
}> = ({ row, print }) => {
  const [opened, setOpen] = useState(false);
  const open = opened && !print;
  const { specification, statements } = row;
  const [only] = statements;
  const several = statements.length > 1;
  if (!only) {
    return null;
  }

  return (
    <>
      <TableRow sx={{ verticalAlign: "top" }}>
        <TableCell>
          {/* The toggle of the statements before the key, as every table of
              the view opens a row */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: `${TOGGLE_GAP}px` }}>
            {!print &&
              (several ? (
                <RowToggle
                  open={open}
                  label={open ? "Hide the statements" : `Show all ${statements.length} statements`}
                  onToggle={() => setOpen(!open)}
                />
              ) : (
                <NoToggle />
              ))}
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                <Box component="span" sx={{ fontWeight: 500 }}>
                  {codeWords(specification.key)}
                </Box>
                {row.differs && (
                  <Tag tone="warning" title="The sources give different values">
                    differs
                  </Tag>
                )}
              </Box>
              {!print &&
                (several ? (
                  <Typography variant="caption" component="div" sx={quiet}>
                    {statements.length} statements
                  </Typography>
                ) : (
                  <StatementWording statement={only} />
                ))}
            </Box>
          </Box>
        </TableCell>
        <ValueCells
          value={row.value}
          display={row.display}
          qualifier={row.qualifier}
          conditions={several ? null : only.conditions}
          sources={row.sources}
          print={print}
        />
      </TableRow>
      {open &&
        statements.map((statement, index) => (
          <TableRow
            key={index}
            sx={{
              verticalAlign: "top",
              backgroundColor: (theme) => alpha(theme.palette.chart.ink, 0.03),
            }}
          >
            <TableCell sx={{ pl: `calc(16px + ${TOGGLE_WIDTH + TOGGLE_GAP}px)` }}>
              <StatementWording statement={statement} />
            </TableCell>
            <ValueCells
              value={statement}
              display={row.display}
              qualifier={statement.qualifier}
              conditions={statement.conditions}
              sources={statement.sources}
            />
          </TableRow>
        ))}
    </>
  );
};

// A row that heads a group of metrics, opening and closing it
const GroupRow: React.FC<{
  label: string;
  count: number;
  open: boolean;
  onToggle?: () => void;
  columns: number;
}> = ({ label, count, open, onToggle, columns }) => (
  <TableRow sx={{ backgroundColor: (theme) => alpha(theme.palette.chart.ink, 0.03) }}>
    <TableCell colSpan={columns} sx={{ py: 0.75 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: `${TOGGLE_GAP}px` }}>
        {onToggle && (
          <RowToggle open={open} label={open ? `Hide ${label}` : `Show ${label}`} onToggle={onToggle} />
        )}
        <Typography component="span" sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
          {label}
        </Typography>
        <Box component="span" sx={{ ...monospaceStyle, ...quiet, fontSize: "0.75rem" }}>
          {count}
        </Box>
      </Box>
    </TableCell>
  </TableRow>
);

/**
 * Specifications as a table: key with the quoted wording beneath, value,
 * unit, qualifier and sources.
 *
 * Each metric is one row: its value, or where its statements differ the
 * range they span, flagged, over the sources of all of them; its toggle
 * opens every statement. The metrics of the curated registry come first;
 * the ones that came verbatim from documents follow under a group of
 * their own, closed where curated ones stand above it. Relative values
 * ("20 times higher accuracy") and statements with no value are left out.
 * Renders nothing without a specification left to show.
 */
const SpecificationTable: React.FC<SpecificationTableProps> = ({
  specifications,
  print = false,
}) => {
  const model = useMemo(() => specificationModel(specifications), [specifications]);
  const [otherOpen, setOtherOpen] = useState(false);

  if (model.curated.length + model.other.length === 0) {
    return null;
  }

  const columns = (print ? PRINT_COLUMNS : COLUMNS).length;
  // The other metrics need a group of their own beneath curated ones only;
  // a printed page shows it open
  const otherGrouped = model.curated.length > 0 && model.other.length > 0;
  const showOther = !otherGrouped || otherOpen || print;

  const rows = (list: SpecificationRow[]) =>
    list.map((row) => (
      <SpecificationRows
        key={`${row.specification.key}|${row.specification.unit ?? ""}`}
        row={row}
        print={print}
      />
    ));

  // The table stands in the frame of every table of the view; a printed
  // page draws it plain
  return (
    <TableContainer
      sx={print ? { overflowX: "auto" } : { ...tableFrame, overflowX: "auto" }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            {(print ? PRINT_COLUMNS : COLUMNS).map((column) => (
              <TableCell
                key={column}
                align={column === "Value" ? "right" : "left"}
                sx={columnHeader}
              >
                {column}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows(model.curated)}
          {otherGrouped && (
            <GroupRow
              label="Other specifications"
              count={model.other.length}
              open={showOther}
              onToggle={print ? undefined : () => setOtherOpen(!otherOpen)}
              columns={columns}
            />
          )}
          {showOther && rows(model.other)}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SpecificationTable;
